import React, { useEffect, useState } from "react";
import { Wallet, ShieldCheck, TriangleAlert, X } from "lucide-react";
import Button from "../common/Button";
import Notice from "../common/Notice";
import { shortAddr } from "./format";
import { displayPair } from "../supernova/supernovaSlots";
import { ADD_STEP } from "./useWalletRegistry";
import {
  withStakeCounts,
  removeBlockReason,
  PRIMARY_DETACH_SUPPORTED,
} from "./walletRegistry";

/**
 * WalletRegistryPanel — the member's verified wallets, and the flow that adds
 * one (ARC-46).
 *
 * WHY MULTI-WALLET EXISTS: a member can stake on one address and trade from
 * others, against a single member-level slot pool. This panel is where they see
 * which addresses the engine has actually verified, what each one holds, and
 * what each one is running.
 *
 * THE ADD FLOW SHOWS THE DOMAIN BEFORE THE WALLET OPENS. The challenge is
 * fetched, then everything stops: the domain, the URI and the full message the
 * wallet will display are on screen, and the member presses the button. A
 * signature prompt that appears unbidden is the pattern this app refuses.
 *
 * NOTHING HERE SIGNS OR MOVES ANYTHING. Attaching a wallet is a free, off-chain
 * signature that proves control of an address; removing one detaches it from
 * the account and changes nothing on-chain. No transaction is constructed
 * anywhere in this file, and the member's own wallet is the only thing that
 * ever signs.
 *
 * COMPLIANCE: addresses, labels, counts of staked Supernovas and automated
 * strategies, and what went wrong. No return, price, projection or promise.
 */

export const WALLET_REGISTRY_DOM_ID = "sa-wallet-registry";

/* One staked Supernova is one automation slot. A wallet the pool did not count
   renders an em dash, never a zero — "we don't know" and "nothing there" are
   different claims, and only one of them is alarming. */
const stakedLabel = (staked) =>
  typeof staked === "number" && staked >= 0 ? `${staked} staked` : "—";

const automatedLabel = (pairs) => {
  const n = Array.isArray(pairs) ? pairs.length : 0;
  if (n === 0) return "No strategies";
  return `${n} automated`;
};

/* ── one row ─────────────────────────────────────────────────────────────── */
const WalletRow = ({ wallet, busy, onRename, onRemove }) => {
  const [label, setLabel] = useState(wallet.label || "");
  /* Follow the engine when it answers with something different — a rename that
     landed elsewhere, or a fresh list after an add. The member's in-progress
     typing is what `dirty` protects. */
  useEffect(() => {
    setLabel(wallet.label || "");
  }, [wallet.label]);

  const dirty = (wallet.label || "") !== label;
  const blocked = removeBlockReason(wallet);
  const pairs = (wallet.automatedPairs || []).map(displayPair);

  return (
    <li className="sa-wreg-row" data-testid="sa-wallet-row" data-address={wallet.address}>
      <div className="sa-wreg-who">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Add a name (optional)"
          aria-label={`Name for wallet ${shortAddr(wallet.address)}`}
          className="sa-wreg-label"
          maxLength={40}
          disabled={busy}
        />
        <span className="sa-wreg-addr tnum" title={wallet.address}>
          {shortAddr(wallet.address)}
        </span>
        {wallet.isPrimary && (
          <span className="sa-wreg-chip" title="The wallet your account was enrolled with.">
            Enrolled wallet
          </span>
        )}
      </div>

      <div className="sa-wreg-facts">
        <span className="sa-wreg-n tnum" data-testid="sa-wallet-staked">
          {stakedLabel(wallet.staked)}
        </span>
        <span
          className="sa-wreg-n"
          data-testid="sa-wallet-automated"
          title={pairs.length ? pairs.join(", ") : undefined}
        >
          {automatedLabel(wallet.automatedPairs)}
        </span>
      </div>

      <div className="sa-wreg-acts">
        {dirty && (
          <Button
            variant="secondary"
            onClick={() => onRename(wallet.address, label)}
            disabled={busy}
            className="!px-3 !py-1.5 !text-[12.5px]"
          >
            {busy ? "Saving…" : "Save name"}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => onRemove(wallet)}
          disabled={busy || Boolean(blocked)}
          title={blocked || "Detach this wallet from your account."}
          className="!px-3 !py-1.5 !text-[12.5px] !text-arc-neg !ring-arc-neg/40"
        >
          Remove
        </Button>
      </div>

      {/* The reason lives next to the disabled control, not in a tooltip only —
          a member on a touch screen has no hover. (ARC-48 removes this.) */}
      {blocked && (
        <p className="sa-wreg-blocked" data-testid="sa-wallet-remove-blocked">
          {blocked}
        </p>
      )}
    </li>
  );
};

/* ── the add flow ────────────────────────────────────────────────────────── */
const AddWalletFlow = ({ registry }) => {
  const [label, setLabel] = useState("");
  const { challenge, addStep, addBusy } = registry;

  if (addStep === ADD_STEP.IDLE && !registry.addError && !registry.added) return null;

  const siwe = challenge?.siwe;

  return (
    <div className="sa-wreg-add" data-testid="sa-wallet-add">
      {registry.addError && (
        <Notice tone="warn" role="alert" data-testid="sa-wallet-add-error" className="mb-3">
          {registry.addError.message}
        </Notice>
      )}

      {registry.added && addStep === ADD_STEP.DONE && (
        <Notice tone="info" role="status" data-testid="sa-wallet-added" className="mb-3">
          <span className="font-semibold">Wallet verified and added.</span> {shortAddr(registry.added)}{" "}
          is now on your account. Any Supernovas it holds are counted in your slot pool.
        </Notice>
      )}

      {addBusy && addStep !== ADD_STEP.SIGNING && (
        <p className="text-caption text-arc-ink-3" role="status">
          {addStep === ADD_STEP.SELECTING
            ? "Choose the wallet you want to add in your wallet extension…"
            : addStep === ADD_STEP.NONCE
            ? "Requesting a sign-in message from the engine…"
            : "Verifying with the engine…"}
        </p>
      )}

      {addStep === ADD_STEP.REVIEW && challenge && (
        <div className="sa-wreg-review" data-testid="sa-wallet-review">
          <p className="sa-wreg-review-h">
            <ShieldCheck size={14} aria-hidden="true" />
            Check this before you sign
          </p>

          <dl className="sa-wreg-kv">
            <dt>Wallet</dt>
            <dd className="tnum" title={challenge.address}>
              {shortAddr(challenge.address)}
            </dd>
            <dt>Asking domain</dt>
            <dd data-testid="sa-siwe-domain">{siwe?.domain || "not stated"}</dd>
            {siwe?.uri && (
              <>
                <dt>URI</dt>
                <dd>{siwe.uri}</dd>
              </>
            )}
          </dl>

          {/* The domain is the only part of the request that says who is
              asking. Agreement, disagreement and "we can't tell" are three
              different answers and each gets its own line — a mismatch is never
              rendered as silence. */}
          {siwe?.matches === true && (
            <p className="sa-wreg-ok" data-testid="sa-siwe-match">
              This matches the site you&apos;re on ({siwe.expected}).
            </p>
          )}
          {siwe?.matches === false && (
            <Notice tone="warn" role="alert" data-testid="sa-siwe-mismatch" className="mt-2">
              <span className="font-semibold">
                This request names a different domain than the site you&apos;re on.
              </span>{" "}
              It says <b>{siwe.domain}</b>; you are on <b>{siwe.expected}</b>. Don&apos;t sign
              unless you know why. Cancel and tell us what you saw.
            </Notice>
          )}
          {siwe?.matches === null && (
            <p className="sa-wreg-warnline" data-testid="sa-siwe-unknown">
              We couldn&apos;t confirm which domain this request comes from. Read the message
              below in your wallet before approving it.
            </p>
          )}

          <p className="sa-wreg-sublabel">The exact message your wallet will show</p>
          <pre className="sa-wreg-msg" data-testid="sa-siwe-message">
            {challenge.message}
          </pre>

          <label className="sa-wreg-namefield">
            <span>Name this wallet (optional)</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Cold storage"
              maxLength={40}
              aria-label="Name this wallet"
            />
          </label>

          <p className="text-caption text-arc-ink-3 mt-2">
            Signing is free and off-chain. It sends no transaction, moves nothing, and gives
            no one permission to touch your funds — it only proves you control this address.
          </p>

          <div className="flex items-center gap-3 flex-wrap mt-3">
            <Button onClick={() => registry.confirmAdd({ label })} disabled={addBusy}>
              {addStep === ADD_STEP.SIGNING ? "Waiting for your wallet…" : "Sign and add wallet"}
            </Button>
            <Button variant="secondary" onClick={registry.cancelAdd} disabled={addBusy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {addStep === ADD_STEP.SIGNING && (
        <p className="text-caption text-arc-ink-3 mt-2" role="status">
          Approve the signature request in your wallet. Nothing is sent until you do.
        </p>
      )}
    </div>
  );
};

/* ── the panel ───────────────────────────────────────────────────────────── */
const WalletRegistryPanel = ({ registry, perWallet = [] }) => {
  const [confirming, setConfirming] = useState(null); // the wallet pending removal

  if (!registry?.supported) {
    return (
      <div className="sa-console p-6" id={WALLET_REGISTRY_DOM_ID} data-testid="sa-wallet-registry">
        <p className="text-caption text-arc-ink-3 m-0" data-testid="sa-wallet-unsupported">
          Managing multiple wallets needs the engine&apos;s wallet registry endpoints, which
          this build can&apos;t reach. Your enrolled wallet is unaffected.
        </p>
      </div>
    );
  }

  const rows = withStakeCounts(registry.wallets || [], perWallet);

  const doRemove = async (wallet) => {
    const ok = await registry.remove(wallet.address);
    if (ok) setConfirming(null);
  };

  return (
    <div className="sa-console" id={WALLET_REGISTRY_DOM_ID} data-testid="sa-wallet-registry">
      <div className="sa-wreg-head">
        <p className="sa-wreg-h">
          <Wallet size={14} aria-hidden="true" />
          Your verified wallets
        </p>
        <p className="sa-wreg-sub">
          Each wallet is verified with its own signature. Slots are pooled across all of them —
          adding a wallet adds slots only if that wallet holds staked Supernovas.
        </p>
      </div>

      {registry.listError && (
        <Notice tone="warn" role="alert" className="mx-6 mb-4" data-testid="sa-wallet-list-error">
          {registry.listError.message}{" "}
          <button type="button" onClick={registry.refresh} className="underline font-semibold">
            Try again
          </button>
        </Notice>
      )}

      {registry.notEnrolled && (
        <p className="px-6 pb-4 text-caption text-arc-ink-3" data-testid="sa-wallet-not-enrolled">
          You haven&apos;t enrolled a wallet yet. Enrollment in &ldquo;Your signal setup&rdquo;
          below creates your account with the engine; further wallets are added here.
        </p>
      )}

      {registry.loading && rows.length === 0 && (
        <p className="px-6 pb-4 text-caption text-arc-ink-3" role="status">
          Reading your verified wallets…
        </p>
      )}

      {rows.length > 0 && (
        <ul className="sa-wreg-list">
          {rows.map((w) => (
            <WalletRow
              key={w.address}
              wallet={w}
              busy={registry.busyAddress === w.address}
              onRename={registry.rename}
              onRemove={setConfirming}
            />
          ))}
        </ul>
      )}

      {registry.actionError && (
        <div className="px-6 pb-4">
          <Notice tone="warn" role="alert" data-testid="sa-wallet-action-error">
            <span className="flex-1">{registry.actionError.message}</span>
            <button
              type="button"
              onClick={registry.clearActionError}
              aria-label="Dismiss"
              className="ml-2 align-top"
            >
              <X size={13} className="text-arc-ink-3" aria-hidden="true" />
            </button>
          </Notice>
        </div>
      )}

      {/* Removal is confirmed in place, and the confirmation says exactly what
          it does and what it does NOT do. Nothing on-chain changes, and no
          funds move — a member should not have to guess that. */}
      {confirming && (
        <div className="px-6 pb-5">
          <Notice
            tone="warn"
            role="alertdialog"
            aria-label="Confirm removing this wallet"
            data-testid="sa-wallet-remove-confirm"
          >
            <p className="text-sm text-arc-warn font-semibold m-0">
              Remove {confirming.label ? `“${confirming.label}” ` : ""}
              {shortAddr(confirming.address)} from your account?
            </p>
            <p className="text-caption text-arc-ink-2 mt-1.5 mb-3">
              This detaches the wallet from your ARCrypto account. Nothing on-chain changes, no
              funds move, and any Supernovas it holds stop counting toward your slot pool.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => doRemove(confirming)}
                disabled={registry.busyAddress === confirming.address}
              >
                {registry.busyAddress === confirming.address ? "Removing…" : "Remove wallet"}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                Keep it
              </Button>
            </div>
          </Notice>
        </div>
      )}

      <div className="px-6 pb-5">
        <AddWalletFlow registry={registry} />
      </div>

      {!PRIMARY_DETACH_SUPPORTED && rows.some((w) => w.isPrimary) && (
        <p className="px-6 pb-5 text-caption text-arc-ink-4 m-0">
          <TriangleAlert size={11} className="inline-block mr-1 -mt-0.5" aria-hidden="true" />
          Your enrolled wallet can&apos;t be detached yet. Everything else on this list can be
          added and removed freely.
        </p>
      )}
    </div>
  );
};

export default WalletRegistryPanel;
